import { useEffect, useRef } from "react";
import { useLocation } from "@/lib/router-compat";
import { X } from "lucide-react";
import { useExitIntent } from "@/hooks/useExitIntent";
import QuickQuoteForm from "@/components/QuickQuoteForm";
import { useExperiment } from "@/hooks/useExperiment";

const EXIT_VARIANTS = ["consult", "guide", "urgency"] as const;
const EXIT_COPY: Record<
  (typeof EXIT_VARIANTS)[number],
  { eyebrow: string; headline: string; sub: string }
> = {
  consult: {
    eyebrow: "Wait — before you go",
    headline: "Get a free fence quote in 24 hours",
    sub: "No pressure. No door-to-door. We'll text or call within 1 business day.",
  },
  guide: {
    eyebrow: "Not ready yet?",
    headline: "Get your free fence buyer's guide",
    sub: "We'll send pricing, sizing, and the questions to ask any installer — no obligation.",
  },
  urgency: {
    eyebrow: "Plan your next step",
    headline: "Lock in your free quote this week",
    sub: "Discuss your property and project scope. Installation timing is confirmed in your written proposal.",
  },
};

/**
 * ExitIntentCapture
 *
 * Shows a one-time bottom sheet (mobile) / centered card (desktop) when the
 * user signals exit intent. Uses sessionStorage so it never re-fires within
 * the same session. Hidden on conversion-flow routes.
 */
const ExitIntentCapture = () => {
  const { pathname } = useLocation();

  const blocked =
    pathname.startsWith("/book") ||
    pathname.startsWith("/contact") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/unsubscribe") ||
    pathname.startsWith("/beat-my-estimate");

  const { triggered, dismiss } = useExitIntent({ enabled: !blocked });
  const { variant: exitVariant, track } = useExperiment(
    "exit_intent_offer",
    EXIT_VARIANTS as unknown as string[],
  );
  const copy =
    EXIT_COPY[exitVariant as (typeof EXIT_VARIANTS)[number]] ?? EXIT_COPY.consult;

  const viewedRef = useRef(false);
  useEffect(() => {
    if (triggered && !blocked && !viewedRef.current) {
      viewedRef.current = true;
      track("offer_view", { variant: exitVariant });
    }
  }, [triggered, blocked, exitVariant, track]);

  if (!triggered || blocked) return null;

  const handleDismiss = () => {
    track("dismiss", { variant: exitVariant });
    dismiss();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-charcoal/60 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={copy.headline}
      onClick={handleDismiss}
    >
      <div
        className="w-full sm:max-w-lg bg-background rounded-t-2xl sm:rounded-2xl shadow-2xl border border-border p-5 sm:p-7 m-0 sm:m-4 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
      >
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-center mb-4">
          <p className="text-primary font-semibold text-[11px] uppercase tracking-widest mb-1">
            {copy.eyebrow}
          </p>
          <h3 className="text-xl sm:text-2xl font-bold text-foreground">
            {copy.headline}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {copy.sub}
          </p>
        </div>
        <QuickQuoteForm
          source="exit-intent"
          variant="stacked"
          tone="white"
          compact
          
          sub
        />
      </div>
    </div>
  );
};

export default ExitIntentCapture;
